import * as React from 'react';
import { Order, Photographer, Region } from '../BookingsCalendarTypes';
import CalendarHeader from './CalendarHeader';
import CalendarBody from './CalendarBody';
import CalendarLoading from './CalendarLoading';
import { getPhotographersForBookingUnbookedOrders } from '../queries/getPhotographersForBookingUnbookedOrders';
import Grid from '@mui/material/Grid';
import { handleError } from '../../../../shared/ErrorCatching';
import NoAvailablePhotographers from './NoAvailablePhotographers';
import useBookingInfo from '../state/useBookingInfo';
import { filterBasedOnProductProperties } from '../state/filterPhotographers';
import useBookingConditions from '../state/useBookingConditions';
import { AreaOrRegion } from '../components/Filters/AreaRegionToggle';

type ExternalProps = {
    user: any;
    selectedOrder: Order;
    availableRegions: Region[];
    isAdvancedMode: boolean;
    onChangeAdvancedMode: () => void;
    hideTravelTimes: boolean;
    handleHideTravelTime: (hideTravelTimes: boolean) => void;
};

type CalendarSectionProps = {
    onChangeBookingDate: (newBookingDate: Date) => void;
    selectedBookingDate: Date;
    onChangeRegionFilter: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    selectedRegion: number;
    photographers: Photographer[];
    selectedProperties: number[];
    handleChangeSelectedProperties: (event: number[]) => void;
    onChangeExecutionTime: (newExecutionTime: number) => void;
    onAreaOrRegionChange: (value: AreaOrRegion) => void;
    shouldUseArea: boolean;
} & ExternalProps;

type ApiResponse = {
    data: any;
    isLoading: boolean;
    isError: boolean;
    error: any;
};

const CalendarSection = ({
    user,
    onChangeAdvancedMode,
    isAdvancedMode,
    onChangeBookingDate,
    selectedBookingDate,
    availableRegions,
    onChangeRegionFilter,
    selectedRegion,
    photographers,
    selectedOrder,
    selectedProperties,
    handleChangeSelectedProperties,
    handleHideTravelTime,
    hideTravelTimes,
    onChangeExecutionTime,
    onAreaOrRegionChange,
    shouldUseArea
}: CalendarSectionProps) => {
    const { bookingInfo, selectedPhotographer, handleChangeBookingInfo, handleChangePhotographer } =
        useBookingInfo(selectedOrder, photographers, selectedProperties, onChangeExecutionTime);

    return (
        <Grid
            item
            xs={12}
            md={8}
            sx={{
                paddingTop: '29px !important'
            }}
            aria-label="calendar-section"
        >
            <CalendarHeader
                user={user}
                shouldUseArea={shouldUseArea}
                onAreaOrRegionChange={onAreaOrRegionChange}
                onChangeAdvancedMode={onChangeAdvancedMode}
                isAdvancedMode={isAdvancedMode}
                onChangeBookingDate={onChangeBookingDate}
                selectedBookingDate={selectedBookingDate}
                bookingInfo={bookingInfo}
                availableRegions={availableRegions}
                onChangeRegionFilter={onChangeRegionFilter}
                selectedRegion={selectedRegion}
                selectedPhotographer={selectedPhotographer}
                selectedOrder={selectedOrder}
                selectedProperties={selectedProperties}
                handleChangeSelectedProperties={handleChangeSelectedProperties}
                onChangeHideTravelTime={handleHideTravelTime}
                hideTravelTimes={hideTravelTimes}
            />
            {photographers.length > 0 ? (
                <CalendarBody
                    bookingInfo={bookingInfo}
                    isAdvancedMode={isAdvancedMode}
                    photographers={photographers}
                    onChangeBookingInfo={handleChangeBookingInfo}
                    selectedOrder={selectedOrder}
                    selectedBookingDate={selectedBookingDate}
                    onChangePhotographer={handleChangePhotographer}
                    hideTravelTimes={hideTravelTimes}
                />
            ) : (
                <NoAvailablePhotographers isAdvancedMode={isAdvancedMode} />
            )}
        </Grid>
    );
};

const CalendarSectionDataLayer = ({
    selectedOrder,
    user,
    availableRegions,
    isAdvancedMode,
    onChangeAdvancedMode,
    hideTravelTimes,
    handleHideTravelTime
}: ExternalProps) => {
    const {
        bookingDate,
        handleChangeBookingDate,
        selectedRegion,
        handleChangeRegionFilter,
        selectedProperties,
        handleChangeSelectedProperties,
        executionTime,
        handleChangeExecutionTime
    } = useBookingConditions(selectedOrder, isAdvancedMode);

    let [shouldUseArea, setShouldUseArea] = React.useState<boolean>(true);

    function handleAreaOrRegionChange(value: AreaOrRegion) {
        let canHasArea = value == AreaOrRegion.Area;
        if (canHasArea !== shouldUseArea) {
            setShouldUseArea(canHasArea);
        }
    }
    
    function handleOnChangeAdvancedMode() {
        if (isAdvancedMode) {
            setShouldUseArea(true);
        }
        onChangeAdvancedMode();
    }

    const { data, isLoading, isError, error }: ApiResponse = getPhotographersForBookingUnbookedOrders(
        selectedOrder.orderId,
        bookingDate,
        isAdvancedMode,
        hideTravelTimes,
        executionTime,
        shouldUseArea ? null : selectedRegion
    );

    if (isError) {
        handleError(error);
    }

    if (isLoading) {
        return (
            <Grid
                item
                xs={12}
                md={8}
                sx={{
                    paddingTop: '29px !important'
                }}
            >
                <CalendarHeader
                    user={user}
                    shouldUseArea={shouldUseArea}
                    onAreaOrRegionChange={handleAreaOrRegionChange}
                    selectedBookingDate={bookingDate}
                    isAdvancedMode={isAdvancedMode}
                    availableRegions={availableRegions}
                    selectedRegion={selectedRegion}
                    selectedOrder={selectedOrder}
                    selectedProperties={selectedProperties}
                    handleChangeSelectedProperties={handleChangeSelectedProperties}
                />
                <CalendarLoading isAdvancedMode={isAdvancedMode} />
            </Grid>
        );
    }

    const { availablePhotographers } = data;

    const filteredPhotographers = isAdvancedMode
        ? availablePhotographers.filter(filterBasedOnProductProperties(selectedProperties))
        : availablePhotographers;

    return (
        <CalendarSection
            key={'calendar-section' + isAdvancedMode + selectedRegion + shouldUseArea}
            shouldUseArea={shouldUseArea}
            onAreaOrRegionChange={handleAreaOrRegionChange}
            user={user}
            onChangeAdvancedMode={handleOnChangeAdvancedMode}
            isAdvancedMode={isAdvancedMode}
            onChangeBookingDate={handleChangeBookingDate}
            selectedBookingDate={bookingDate}
            availableRegions={availableRegions}
            onChangeRegionFilter={handleChangeRegionFilter}
            selectedRegion={selectedRegion}
            photographers={filteredPhotographers}
            selectedOrder={selectedOrder}
            selectedProperties={selectedProperties}
            handleChangeSelectedProperties={handleChangeSelectedProperties}
            handleHideTravelTime={handleHideTravelTime}
            hideTravelTimes={hideTravelTimes}
            onChangeExecutionTime={handleChangeExecutionTime}
        />
    );
};

export default CalendarSectionDataLayer;
